import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, X, Check, Loader2, Package } from 'lucide-react';
import api from '@/utils/api';
import { showError, showSuccess } from '@/utils/toast';

const emptyProduct = {
  name: '',
  category: '',
  min_amount: '',
  max_amount: '',
  interest_rate_min: '',
  interest_rate_max: '',
  tenure_min: '',
  tenure_max: '',
  processing_fee: '',
  lender_ids: [],
  is_active: true,
};

const CATEGORIES = ['Personal', 'Business', 'Home', 'Auto', 'Education', 'LAP', 'Credit Card', 'Other'];

const ProductManager = ({ readOnly = false }) => {
  const [products, setProducts] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(emptyProduct);

  useEffect(() => {
    Promise.all([fetchProducts(), fetchLenders()]).finally(() => setLoading(false));
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await api.get('/products');
      setProducts(res.data || []);
    } catch (e) {
      showError('Failed to load products');
    }
  };

  const fetchLenders = async () => {
    try {
      const res = await api.get('/lenders');
      setLenders(res.data || []);
    } catch (e) { console.warn(e?.message); }
  };

  const startNew = () => {
    setForm(emptyProduct);
    setEditing('new');
  };

  const startEdit = (p) => {
    setForm({
      ...emptyProduct,
      ...p,
      min_amount: p.min_amount ?? '',
      max_amount: p.max_amount ?? '',
      interest_rate_min: p.interest_rate_min ?? '',
      interest_rate_max: p.interest_rate_max ?? '',
      tenure_min: p.tenure_min ?? '',
      tenure_max: p.tenure_max ?? '',
      processing_fee: p.processing_fee ?? '',
      lender_ids: p.lender_ids || [],
    });
    setEditing(p.id);
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(emptyProduct);
  };

  const toNumberOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

  const handleSave = async () => {
    if (!form.name || !form.name.trim()) {
      showError('Product name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category || null,
        min_amount: toNumberOrNull(form.min_amount),
        max_amount: toNumberOrNull(form.max_amount),
        interest_rate_min: toNumberOrNull(form.interest_rate_min),
        interest_rate_max: toNumberOrNull(form.interest_rate_max),
        tenure_min: toNumberOrNull(form.tenure_min),
        tenure_max: toNumberOrNull(form.tenure_max),
        processing_fee: form.processing_fee || null,
        lender_ids: form.lender_ids || [],
        is_active: !!form.is_active,
      };
      if (editing === 'new') {
        await api.post('/products', payload);
        showSuccess('Product created');
      } else {
        await api.put(`/products/${editing}`, payload);
        showSuccess('Product updated');
      }
      await fetchProducts();
      cancelEdit();
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try {
      await api.delete(`/products/${id}`);
      showSuccess('Product deleted');
      fetchProducts();
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to delete product');
    }
  };

  const toggleLender = (id) => {
    setForm((f) => ({
      ...f,
      lender_ids: f.lender_ids.includes(id) ? f.lender_ids.filter((x) => x !== id) : [...f.lender_ids, id],
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="product-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Package size={20} /> Loan Products
          </h2>
          <p className="text-sm text-muted-foreground">Manage loan products available for lead creation.</p>
        </div>
        {!readOnly && editing === null && (
          <button
            data-testid="add-product-button"
            onClick={startNew}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            <Plus size={18} /> Add Product
          </button>
        )}
      </div>

      {editing !== null && !readOnly && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="product-form">
          <h3 className="font-semibold mb-4 text-foreground">{editing === 'new' ? 'New Product' : 'Edit Product'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Product Name *</label>
              <input data-testid="product-name-input" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-border bg-background" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-border bg-background">
                <option value="">Select…</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Min Loan Amount (₹)</label>
              <input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-border bg-background" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max Loan Amount (₹)</label>
              <input type="number" value={form.max_amount} onChange={(e) => setForm({ ...form, max_amount: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-border bg-background" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Min Interest Rate (%)</label>
              <input type="number" step="0.01" value={form.interest_rate_min} onChange={(e) => setForm({ ...form, interest_rate_min: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-border bg-background" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max Interest Rate (%)</label>
              <input type="number" step="0.01" value={form.interest_rate_max} onChange={(e) => setForm({ ...form, interest_rate_max: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-border bg-background" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Min Tenure (months)</label>
              <input type="number" value={form.tenure_min} onChange={(e) => setForm({ ...form, tenure_min: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-border bg-background" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max Tenure (months)</label>
              <input type="number" value={form.tenure_max} onChange={(e) => setForm({ ...form, tenure_max: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-border bg-background" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Processing Fee</label>
              <input type="text" placeholder="e.g., 1.5% + GST" value={form.processing_fee} onChange={(e) => setForm({ ...form, processing_fee: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-border bg-background" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Lender Mapping</label>
              {lenders.length === 0 ? (
                <p className="text-sm text-muted-foreground">No lenders configured yet. Add lenders in the Lenders module first.</p>
              ) : (
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border border-border rounded-lg">
                  {lenders.map((l) => {
                    const selected = form.lender_ids.includes(l.id);
                    return (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => toggleLender(l.id)}
                        className={`px-3 py-1.5 rounded-full text-xs border ${
                          selected ? 'bg-primary text-white border-primary' : 'bg-background text-foreground border-border hover:bg-muted'
                        }`}
                      >
                        {selected && <Check size={12} className="inline mr-1" />}
                        {l.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4" />
                <span className="text-sm">Active (available for lead creation)</span>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={cancelEdit} className="px-4 py-2 border border-border rounded-lg hover:bg-muted">
              <X size={16} className="inline mr-1" /> Cancel
            </button>
            <button
              data-testid="save-product-button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {saving && <Loader2 className="animate-spin" size={16} />}
              Save
            </button>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Category</th>
                <th className="text-left px-4 py-3 font-semibold">Amount</th>
                <th className="text-left px-4 py-3 font-semibold">Interest</th>
                <th className="text-left px-4 py-3 font-semibold">Tenure</th>
                <th className="text-left px-4 py-3 font-semibold">Fee</th>
                <th className="text-left px-4 py-3 font-semibold">Lenders</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                {!readOnly && <th className="text-right px-4 py-3 font-semibold">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {products.length === 0 ? (
                <tr>
                  <td colSpan={readOnly ? 8 : 9} className="px-4 py-8 text-center text-muted-foreground">
                    No products yet. {!readOnly && 'Click "Add Product" to create one.'}
                  </td>
                </tr>
              ) : (
                products.map((p) => (
                  <tr key={p.id} data-testid={`product-row-${p.id}`}>
                    <td className="px-4 py-3 font-medium text-foreground">{p.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.category || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {p.min_amount || p.max_amount
                        ? `₹${(p.min_amount || 0).toLocaleString('en-IN')} - ₹${(p.max_amount || 0).toLocaleString('en-IN')}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {p.interest_rate_min || p.interest_rate_max
                        ? `${p.interest_rate_min ?? '–'}% - ${p.interest_rate_max ?? '–'}%`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {p.tenure_min || p.tenure_max ? `${p.tenure_min ?? '–'}-${p.tenure_max ?? '–'} mo` : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{p.processing_fee || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.lender_ids?.length || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {!readOnly && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            data-testid={`edit-product-${p.id}`}
                            onClick={() => startEdit(p)}
                            className="p-2 rounded-lg hover:bg-muted"
                            title="Edit"
                          >
                            <Edit3 size={16} className="text-muted-foreground" />
                          </button>
                          <button
                            data-testid={`delete-product-${p.id}`}
                            onClick={() => handleDelete(p.id)}
                            className="p-2 rounded-lg hover:bg-destructive/10"
                            title="Delete"
                          >
                            <Trash2 size={16} className="text-destructive" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductManager;
